import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Mic } from "lucide-react";
import { UserSession } from "@/types/session";
import { ChatEntry } from "@/types/chat";
import { cn } from "@/lib/utils";
import { ChatPlusMenu } from "@/components/chat/ChatPlusMenu";

type ChatInputProps = {
  session?: UserSession | null;
  /** Session may be null before the first message; parent creates/opens the session on send. */
  onSubmit: (message: string, session: UserSession | null | undefined) => void;
  isLoading: boolean;
  isDisabled: boolean;
  onVoiceClick?: () => void;
  chatEntry?: ChatEntry;
  /** When set, shows a + control that opens a file picker and forwards chosen files. */
  onFilesSelected?: (files: FileList) => void;
  placeholder?: string;
  /** Standalone hero usage (no chat history above): drops the top divider so the input reads as a card. */
  hero?: boolean;
  /** One-time prefill (e.g. navigation from Strategic Decision Partner). */
  initialDraft?: string;
  /** Fired on every draft change so Quick Answer clicks can prefer typed input. */
  onDraftChange?: (draft: string) => void;
  /** Increment to clear the textarea (e.g. after a Quick Answer chip sends). */
  draftClearKey?: number;
};

export const ChatInput = (props: ChatInputProps) => {
  const {
    session,
    onSubmit,
    isLoading,
    isDisabled,
    onVoiceClick,
    chatEntry,
    onFilesSelected,
    placeholder,
    hero,
    initialDraft,
    onDraftChange,
    draftClearKey,
  } = props;

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [message, setMessage] = useState(() => initialDraft?.trim() ?? "");
  const prevSessionIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  useEffect(() => {
    onDraftChange?.(message);
  }, [message, onDraftChange]);

  useEffect(() => {
    if (draftClearKey === undefined || draftClearKey === 0) return;
    setMessage("");
  }, [draftClearKey]);

  useEffect(() => {
    const sid = session?.id;
    const prev = prevSessionIdRef.current;
    if (prev !== undefined && sid !== undefined && prev !== sid) {
      setMessage("");
    }
    prevSessionIdRef.current = sid;
  }, [session?.id]);

  const removeDangerousHtml = (input: string): string => {
    return input
      .replace(/<[^>]*>/g, "")
      .replace(/javascript:/gi, "")
      .replace(/data:/gi, "")
      .replace(/vbscript:/gi, "")
      .replace(/^[<>]+/, "")
      .replace(/^[<>]{2,}/, "");
  };

  const handleSendMessage = async () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage === "") return;
    const cleanedMessage = removeDangerousHtml(trimmedMessage);
    onSubmit(cleanedMessage, session ?? null);
    setMessage("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div
      className={cn(
        "flex min-h-16 shrink-0 items-end space-x-3 px-4 md:px-6",
        hero ? "py-3" : "border-t pt-4"
      )}
      onKeyPress={handleKeyPress}
    >
      <ChatPlusMenu onFilesSelected={onFilesSelected} isDisabled={isLoading || isDisabled} />
      <textarea
        id="message"
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={placeholder ?? "Share what's in your heart..."}
        className="text-sm md:text-base w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-bodhi-orange disabled:cursor-not-allowed focus:border-transparent resize-none overflow-hidden min-h-[48px] max-h-[200px]"
        disabled={isLoading || isDisabled}
        rows={1}
      />

      <button
        onClick={handleSendMessage}
        disabled={!message.trim() || isLoading || isDisabled}
        className="w-10 h-10 bg-bodhi-teal self-center rounded-full flex items-center justify-center text-white hover:bg-bodhi-teal/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
      >
        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
      </button>

      {onVoiceClick && (
        <button
          onClick={onVoiceClick}
          className="w-11 h-11 self-center rounded-full flex items-center justify-center text-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          <Mic className="w-5 h-5 text-bodhi-teal" />
        </button>
      )}
    </div>
  );
};
